import {
	ClockCircleOutlined,
	CloseOutlined,
	FileExcelOutlined,
	HistoryOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { Modal, Skeleton } from 'antd';
import { getApi } from 'apis';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList as List, ListOnItemsRenderedProps } from 'react-window';
import styles from './index.module.scss';

interface UploadHistoryItem {
	id: string;
	fileName: string;
	noOfEmployees: number;
	description: string;
	fileUploadedAt: string;
	uploadedBy: string;
	fileSource: string;
}

interface UploadHistoryModalProps {
	open: boolean;
	onClose: () => void;
	initialTotal?: number;
}

interface UploadStats {
	newCount: number;
	updatedCount: number;
}

const PAGE_SIZE = 20;
const ROW_HEIGHT = 118;
const MAX_LIST_HEIGHT = 420;

const parseUploadStats = (description: string): UploadStats | null => {
	const match = description.match(
		/(\d+)\s+numbers? of employees were added as new and (\d+)\s+number of employees information got updated/i
	);

	if (!match) return null;

	return {
		newCount: parseInt(match[1], 10),
		updatedCount: parseInt(match[2], 10),
	};
};

const UploadHistoryModal: FC<UploadHistoryModalProps> = ({
	open,
	onClose,
	initialTotal = 0,
}) => {
	const [items, setItems] = useState<UploadHistoryItem[]>([]);
	const [total, setTotal] = useState(initialTotal);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const pageRef = useRef(1);
	const hasMoreRef = useRef(true);
	const fetchingRef = useRef(false);

	const fetchPage = useCallback(async (page: number, append: boolean) => {
		if (fetchingRef.current) return;
		fetchingRef.current = true;

		try {
			if (page === 1) {
				setLoading(true);
			} else {
				setLoadingMore(true);
			}

			const response = await getApi('/employee/upload-history', {
				page,
				pageSize: PAGE_SIZE,
				fileSource: 'EMPLOYEE',
			});

			const newData: UploadHistoryItem[] = response.data.data || [];
			const newTotal: number = response.data.total || 0;

			setTotal(newTotal);
			setItems((prev) => (append ? [...prev, ...newData] : newData));
			pageRef.current = page;
			hasMoreRef.current = page * PAGE_SIZE < newTotal;
		} finally {
			setLoading(false);
			setLoadingMore(false);
			fetchingRef.current = false;
		}
	}, []);

	useEffect(() => {
		if (open) {
			pageRef.current = 1;
			hasMoreRef.current = true;
			setItems([]);
			setTotal(initialTotal);
			fetchPage(1, false);
		}
	}, [open, fetchPage, initialTotal]);

	const listHeight = useMemo(() => {
		const count = loading ? 3 : Math.max(items.length, total > 0 ? 1 : 0);
		return Math.min(MAX_LIST_HEIGHT, Math.max(count * ROW_HEIGHT, ROW_HEIGHT));
	}, [items.length, loading, total]);

	const handleItemsRendered = useCallback(
		({ visibleStopIndex }: ListOnItemsRenderedProps) => {
			if (
				!loading &&
				!loadingMore &&
				hasMoreRef.current &&
				visibleStopIndex >= items.length - 3
			) {
				fetchPage(pageRef.current + 1, true);
			}
		},
		[items.length, loading, loadingMore, fetchPage]
	);

	const renderStats = (item: UploadHistoryItem) => {
		const stats = parseUploadStats(item.description);

		if (!stats) {
			return (
				<p className={styles['history-card__fallback']}>
					{item.description}
				</p>
			);
		}

		return (
			<div className={styles['history-card__stats']}>
				<span
					className={`${styles['history-card__stat']} ${styles['history-card__stat--new']}`}
				>
					{stats.newCount} new
				</span>
				<span
					className={`${styles['history-card__stat']} ${styles['history-card__stat--updated']}`}
				>
					{stats.updatedCount} updated
				</span>
			</div>
		);
	};

	const renderRow = ({
		index,
		style,
	}: {
		index: number;
		style: React.CSSProperties;
	}) => {
		const item = items[index];

		if (!item) {
			return (
				<div style={style} className={styles['history-row']}>
					<div className={styles['history-card']}>
						<Skeleton active paragraph={{ rows: 2 }} />
					</div>
				</div>
			);
		}

		return (
			<div style={style} className={styles['history-row']}>
				<div className={styles['history-card']}>
					<div className={styles['history-card__top']}>
						<div className={styles['history-card__icon-wrap']}>
							<FileExcelOutlined />
						</div>
						<div className={styles['history-card__content']}>
							<div className={styles['history-card__title-row']}>
								<span
									className={styles['history-card__filename']}
									title={item.fileName}
								>
									{item.fileName}
								</span>
								<span className={styles['history-card__count']}>
									{item.noOfEmployees} employees
								</span>
							</div>
							<div className={styles['history-card__meta']}>
								<span className={styles['history-card__meta-item']}>
									<UserOutlined />
									{item.uploadedBy}
								</span>
								<span className={styles['history-card__meta-item']}>
									<ClockCircleOutlined />
									{dayjs(item.fileUploadedAt).format(
										'DD MMM YYYY, hh:mm A'
									)}
								</span>
							</div>
							{renderStats(item)}
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			destroyOnClose
			centered={false}
			width={640}
			closable={false}
			className={styles['upload-history-modal']}
			title={null}
		>
			<div className={styles['modal-header']}>
				<div className={styles['modal-header__brand']}>
					<div className={styles['modal-header__icon']}>
						<HistoryOutlined />
					</div>
					<div className={styles['modal-header__text']}>
						<h3 className={styles['modal-header__title']}>
							Upload history
						</h3>
						<span className={styles['modal-header__subtitle']}>
							Employee file imports
						</span>
					</div>
				</div>
				<div className={styles['modal-header__actions']}>
					<span className={styles['modal-header__pill']}>
						{total} upload{total !== 1 ? 's' : ''}
					</span>
					<button
						type="button"
						className={styles['modal-header__close']}
						onClick={onClose}
						aria-label="Close"
					>
						<CloseOutlined />
					</button>
				</div>
			</div>

			<div className={styles['modal-body']}>
				{loading && items.length === 0 ? (
					<div className={styles['loading-container']}>
						{Array.from({ length: 3 }).map((_, index) => (
							<Skeleton
								key={index}
								active
								paragraph={{ rows: 2 }}
								className={styles['skeleton-item']}
							/>
						))}
					</div>
				) : !loading && items.length === 0 ? (
					<div className={styles['empty-state']}>
						<HistoryOutlined className={styles['empty-state__icon']} />
						<p className={styles['empty-state__title']}>
							No uploads yet
						</p>
						<p className={styles['empty-state__text']}>
							Imported employee files will appear here.
						</p>
					</div>
				) : (
					<div className={styles['list-wrapper']}>
						<List
							height={listHeight}
							itemCount={total}
							itemSize={ROW_HEIGHT}
							width="100%"
							onItemsRendered={handleItemsRendered}
						>
							{renderRow}
						</List>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default UploadHistoryModal;
